import { Icon } from '@chakra-ui/react';
import { HiOutlineDocumentReport, HiUsers } from 'react-icons/hi';
import {
	MdAddShoppingCart,
	MdHome,
	MdInsertChartOutlined,
	MdLeaderboard,
	MdLock,
	MdOutlineLaptopMac,
	MdPeopleOutline,
	MdShoppingCart,
} from 'react-icons/md';

import React from 'react';
import { MdOutlineAdminPanelSettings } from 'react-icons/md';
import {
	FaTasks,
	FaWpforms,
	FaRegCalendarCheck,
	FaRegCopy,
	FaList,
	FaPhone,
	FaWhatsapp,
	FaHandshake,
	FaFileAlt,
} from 'react-icons/fa';
import { FaCreativeCommonsBy } from 'react-icons/fa';
import { MdCampaign } from 'react-icons/md';
import { FaClipboardUser, FaSquarePlus } from 'react-icons/fa6';

import { ROLE_PATH } from "./roles";
// Meta WhatsApp V2. NOT views/admin/whatsapp-v2 — that folder is the older
// Baileys stack and is already taken.
import MetaWhatsappInbox from "views/admin/meta-whatsapp";
import MetaWhatsappTemplates from 'views/admin/meta-whatsapp/assignments/metaTemplates/MetaWhatsappTemplates';
import CreateMetaWhatsappTemplate from 'views/admin/meta-whatsapp/assignments/metaTemplates/CreateMetaWhatsappTemplate';
import CampaignsList from "views/admin/meta-campaigns";
import CampaignDetail from "views/admin/meta-campaigns/detail";
import CampaignWizard from "views/admin/meta-campaigns/wizard";
// Meta settings are a TAB of the WhatsApp settings page, not a page of their
// own; the old standalone path redirects there.
import WhatsappSettingsHub, {
	MetaSettingsRedirect,
} from "views/admin/whatsapp/WhatsappSettings/SettingsHub";
import TakeSurvey from "views/admin/survey/TakeSurvey";
import { RiBillLine, RiPlugLine } from "react-icons/ri";

// ========================== Lazy Imports ==========================
// Dashboard
const MainDashboard = React.lazy(() => import('views/admin/default'));
const DashboardV2 = React.lazy(() => import('views/admin/dashboard-v2'));
const LeaveManagement = React.lazy(() => import('views/admin/leave/index'));

// Leads & Deals
const LeadScreen = React.lazy(() => import('views/admin/lead-v2'));
// const NewLeadsScreen = React.lazy(() => import('views/admin/new-leads'));
const LeadPoolAdmin = React.lazy(() => import('views/admin/leadAdmin'));
const LeadPoolAgent = React.lazy(() => import('views/admin/leadPool-v2'));
const BuyNewLeads = React.lazy(() => import('views/admin/new-leadPool-agent'));
const DealsScreen = React.lazy(() => import('views/admin/deals'));
const EvaluationScreen = React.lazy(
	() => import('views/admin/evalution/EvaluationLayout'),
);
const IntegrationsGuide = React.lazy(() => import("views/admin/integerations/index"));
const LeadsIntegration = React.lazy(() => import("views/admin/integerations/LeadsIntegeration"));
const ListingsIntegration = React.lazy(() => import("views/admin/integerations/ListingIntegerations"));
const FinanceScreen = React.lazy(
	() => import('views/admin/finance/FinanceLayout'),
);
const SharedDealsScreen = React.lazy(
	() => import('views/admin/deals/SharedDealsScreen'),
);
const CallFeedback = React.lazy(
	() => import('views/admin/lead-v2/callFeedback/index'),
);
const CloseDealScreen = React.lazy(
	() => import('views/admin/deals/DealsScreen'),
);
const LeadAnalytics = React.lazy(
	() => import('views/admin/lead-v2/leadAnalytics'),
);
const UserProfileDetails = React.lazy(
	() => import('views/admin/users-v2/components/UserProfile'),
);

// Hiring
const Hiring = React.lazy(() => import('views/admin/hiring'));
const HiringDashboard = React.lazy(
	() => import('views/admin/hiring/hiringDashboard'),
);
const Candidates = React.lazy(() => import('views/admin/hiring/candidates'));
const ShortListedCandidates = React.lazy(
	() => import('views/admin/hiring/shortListedCandidates'),
);
const InterviewScreen = React.lazy(
	() => import('views/admin/hiring/interview/InterviewScreen'),
);
const InterviewedCandidates = React.lazy(
	() => import('views/admin/hiring/interviewedCandidates'),
);
const OfferLetter = React.lazy(
	() => import('views/admin/hiring/interviewedCandidates/OfferLetter'),
);
const OfferView = React.lazy(
	() => import('views/admin/hiring/interviewedCandidates/OfferView'),
);
const Positions = React.lazy(() => import('views/admin/hiring/positions'));
const InterviewedRound = React.lazy(
	() => import('views/admin/hiring/interviewedCandidates/Rounds/index'),
);
// Attendance
const AttendanceV2 = React.lazy(
	() => import('views/admin/attendance/AttendenceV2'),
);
const SipLeaderboard = React.lazy(
	() => import('views/admin/sip/component/LeaderBoard/index'),
);
const SipMyRecordings = React.lazy(
	() => import('views/admin/sip/component/MyRecordings/index'),
);
const AttendanceDashboard = React.lazy(
	() => import('views/admin/attendance/components/dashboard'),
);
const Employees = React.lazy(
	() => import('views/admin/attendance/components/employees'),
);
const Records = React.lazy(
	() => import('views/admin/attendance/components/records'),
);
const MyAttendance = React.lazy(
	() => import('views/admin/attendance/components/myAttendance'),
);

// Invoice & Expenses
const InvoiceModule = React.lazy(() => import('views/admin/invoice'));
const DeveloperInvoices = React.lazy(
	() => import('views/admin/invoice/developers/DeveloperInvoices'),
);
const SingleInvoice = React.lazy(() => import('views/admin/invoice/View'));
const AddEntry = React.lazy(() => import('views/admin/invoice/AddEntry'));
const InvoiceSettings = React.lazy(() => import('views/admin/invoice/InvoiceSettings'));
const ManualInvoices = React.lazy(() => import('views/admin/invoice/ManualInvoices'));
const AllInvoices = React.lazy(() => import('views/admin/invoice/AllInvoices'));
const PayrollSalariedUsers = React.lazy(
	() => import('views/admin/payroll/SalariedUsers'),
);
const PayrollCommissionUsers = React.lazy(
	() => import('views/admin/payroll/CommissionUsers'),
);
const EmployeePayslip = React.lazy(
	() => import('views/admin/payroll/components/EmployeePayrollDetails'),
);
const CommissionEmployeePayslip = React.lazy(
	() =>
		import(
			'views/admin/payroll/CommissionUsers/ComissionEmployeePayrollDetails'
		),
);
const UserPayroll = React.lazy(
	() => import('views/admin/payroll/UserPayroll/index'),
);

//Evalution
// const Evalution = React.lazy(() => imporviews/admin/evalution/user-evalution/indexdex'));
const UserEvalution = React.lazy(
	() => import('views/admin/evalution/user-evalution/index'),
);
const MyEvaluation = React.lazy(
	() => import('views/admin/evalution/my-evalution/index'),
);
const EvaluationForm = React.lazy(
	() => import('views/admin/evalution/user-evalution/EvaluationForm'),
);
const EditEvaluationForm = React.lazy(
	() => import('views/admin/evalution/user-evalution/EditEvaluationForm'),
);
const EvaluateSettings = React.lazy(
	() => import('views/admin/evalution/settings/index'),
);
const Teams = React.lazy(() => import('views/admin/evalution/teams/index'));

// Finance
const IncomingCash = React.lazy(
	() => import('views/admin/finance/incoming-balance/index'),
);
const OutgoingCash = React.lazy(
	() => import('views/admin/finance/outgoing-expense/index'),
);
const FinanceSettings = React.lazy(
	() => import('views/admin/finance/settings/index'),
);
const EmployeeLoans = React.lazy(
	() => import('views/admin/finance/employee-laon/index'),
);
const EmployeeLoanDetails = React.lazy(
	() => import('views/admin/finance/employee-laon/loan-details/index'),
);

// Finance Core (accounting / GL)
const FinanceCoreDashboard = React.lazy(
	() => import('views/admin/finance-core/index'),
);
const ChartOfAccounts = React.lazy(
	() => import('views/admin/finance-core/chart-of-accounts/index'),
);
const JournalEntries = React.lazy(
	() => import('views/admin/finance-core/journal-entries/index'),
);
const DeveloperInvoicesFC = React.lazy(
	() => import('views/admin/finance-core/invoices/index'),
);
const CollectionsFC = React.lazy(
	() => import('views/admin/finance-core/collections/index'),
);
const ChequesFC = React.lazy(
	() => import('views/admin/finance-core/cheques/index'),
);
const ReceivablesFC = React.lazy(
	() => import('views/admin/finance-core/receivables/index'),
);
const CommissionCenterFC = React.lazy(
	() => import('views/admin/finance-core/commissions/index'),
);
const CommissionBatchesFC = React.lazy(
	() => import('views/admin/finance-core/commission-batches/index'),
);
const CommissionReportFC = React.lazy(
	() => import('views/admin/finance-core/commission-report/index'),
);
const ExpensesFC = React.lazy(
	() => import('views/admin/finance-core/expenses/index'),
);
const SuppliersFC = React.lazy(
	() => import('views/admin/finance-core/suppliers/index'),
);
const ProcurementFC = React.lazy(
	() => import('views/admin/finance-core/purchase-requests/index'),
);
const PurchaseOrdersFC = React.lazy(
	() => import('views/admin/finance-core/purchase-orders/index'),
);
const APInvoicesFC = React.lazy(
	() => import('views/admin/finance-core/ap-invoices/index'),
);
const PaymentsOutFC = React.lazy(
	() => import('views/admin/finance-core/payment-vouchers/index'),
);
const EmployeeFinanceFC = React.lazy(
	() => import('views/admin/finance-core/employee-finance/index'),
);
const PayrollPostingsFC = React.lazy(
	() => import('views/admin/finance-core/payroll-postings/index'),
);
const TreasuryFC = React.lazy(
	() => import('views/admin/finance-core/treasury/index'),
);
const BankFC = React.lazy(
	() => import('views/admin/finance-core/bank/index'),
);
const CardsFC = React.lazy(
	() => import('views/admin/finance-core/cards/index'),
);
const ObligationsFC = React.lazy(
	() => import('views/admin/finance-core/obligations/index'),
);
const ContractsFC = React.lazy(
	() => import('views/admin/finance-core/contracts/index'),
);
const TreasuryReportsFC = React.lazy(
	() => import('views/admin/finance-core/treasury-reports/index'),
);
const RecognitionsFC = React.lazy(
	() => import('views/admin/finance-core/recognitions/index'),
);
const AssetsFC = React.lazy(
	() => import('views/admin/finance-core/assets/index'),
);
const InventoryFC = React.lazy(
	() => import('views/admin/finance-core/inventory/index'),
);
const AssetReportsFC = React.lazy(
	() => import('views/admin/finance-core/asset-reports/index'),
);
const AllocationsFC = React.lazy(
	() => import('views/admin/finance-core/allocations/index'),
);
const ProjectsFC = React.lazy(
	() => import('views/admin/finance-core/projects/index'),
);
const CostingDashboardFC = React.lazy(
	() => import('views/admin/finance-core/costing-dashboard/index'),
);
const ProfitabilityFC = React.lazy(
	() => import('views/admin/finance-core/profitability/index'),
);
const CreditControlFC = React.lazy(
	() => import('views/admin/finance-core/credit-control/index'),
);
const EquityFC = React.lazy(
	() => import('views/admin/finance-core/equity/index'),
);
const TaxCodesFC = React.lazy(
	() => import('views/admin/finance-core/tax-codes/index'),
);
const TaxReturnsFC = React.lazy(
	() => import('views/admin/finance-core/tax-returns/index'),
);
const CurrencyFC = React.lazy(
	() => import('views/admin/finance-core/currency/index'),
);
const PeriodCloseFC = React.lazy(
	() => import('views/admin/finance-core/period-close/index'),
);
const ImportsFC = React.lazy(
	() => import('views/admin/finance-core/imports/index'),
);
const RiskFC = React.lazy(
	() => import('views/admin/finance-core/risk/index'),
);
const DashboardsFC = React.lazy(
	() => import('views/admin/finance-core/dashboards/index'),
);
const FinancialReportsFC = React.lazy(
	() => import('views/admin/finance-core/financial-reports/index'),
);
const BudgetsFC = React.lazy(
	() => import('views/admin/finance-core/budgets/index'),
);
const ApprovalsFC = React.lazy(
	() => import('views/admin/finance-core/approvals/index'),
);
const GovernanceFC = React.lazy(
	() => import('views/admin/finance-core/governance/index'),
);
const FinancialCalendarFC = React.lazy(
	() => import('views/admin/finance-core/calendar/index'),
);

const DeveloperDetails = React.lazy(
	() => import('views/admin/developers/components/DeveloperView'),
);

const BankAccount = React.lazy(
	() => import('views/admin/bankAccountsV2/index'),
);

const DeveloperScreen = React.lazy(
	() => import('views/admin/invoice/developers/index'),
);
const ProjectScreen = React.lazy(
	() => import('views/admin/developers/projects/index'),
);

// Listing
const Listing = React.lazy(() => import('views/admin/Listing'));
const ClientListings = React.lazy(
	() => import('views/admin/Listing/client-listings'),
);
const PropertyView = React.lazy(
	() =>
		import(
			'views/admin/Listing/client-listings/_component/details/PropertyView'
		),
);
const AddListing = React.lazy(
	() => import('views/admin/Listing/Component/AddListing'),
);
const ViewListing = React.lazy(
	() => import('views/admin/Listing/Component/ViewLisitng'),
);
const UpdateListing = React.lazy(
	() => import('views/admin/Listing/Component/UpdateListing'),
);
const SettingPage = React.lazy(
	() => import('views/admin/Listing/Component/settings'),
);

const AllListing = React.lazy(
	() => import('views/admin/Listing/Component/AllListing'),
);
const MyListing = React.lazy(
	() => import('views/admin/Listing/Component/MyListing'),
);
const ViewRequestListing = React.lazy(
	() => import('views/admin/Listing/Component/ViewRequest'),
);
const PendingListing = React.lazy(
	() => import('views/admin/Listing/Component/PendingListings'),
);
const PendingViewRequest = React.lazy(
	() => import('views/admin/Listing/Component/ViewRequest/ViewRequest'),
);
const ApprovedViewRequest = React.lazy(
	() => import('views/admin/Listing/Component/ViewRequest/ApprovedRequest'),
);
const RejectedViewRequest = React.lazy(
	() => import('views/admin/Listing/Component/ViewRequest/RejectRequest'),
);
const SubUnitType = React.lazy(
	() =>
		import(
			'views/admin/Listing/Component/settings/components/ListingUnitType/SubComponent/SubUnitType'
		),
);

const SecretConfiguration = React.lazy(
	() => import('views/admin/adminSetting/secretConfig/index'),
);
const AdConversions = React.lazy(
	() => import('views/admin/adminSetting/adConversions/index'),
);

// Survey
const Survey = React.lazy(() => import('views/admin/survey'));
const LeaderBoard = React.lazy(() => import('views/admin/survey/LeaderBoard'));
const CreateSurvey = React.lazy(
	() => import('views/admin/survey/CreateSurvey'),
);
const ViewSurveyResponse = React.lazy(
	() => import('views/admin/survey/ViewSurveyResponse'),
);
const SurveyDashboard = React.lazy(
	() => import('views/admin/survey/Component/SurveyDashboard'),
);
const SurveyManage = React.lazy(
	() => import('views/admin/survey/Component/ManageSurveys'),
);
// Whatsapp
const AdminWhatsapp = React.lazy(
	() => import('views/admin/whatsapp/AdminWhatsapp'),
);

const WhatsappInstances = React.lazy(
	() => import('views/admin/whatsapp-v2/Instances'),
);

const UserWhatsappInstance = React.lazy(
	() => import('views/admin/whatsapp-v2/UserWhatsapp'),
);
const UserWhatsappChat = React.lazy(
	() => import('views/admin/whatsapp/UserWhatsapp'),
);
const WhatsappTemplates = React.lazy(
	() => import('views/admin/whatsapp/WhatsappSettings/Templates'),
);
const EvaluationTemplates = React.lazy(
	() => import('views/admin/evalution/evaluation-templates/index'),
);
const CreateWhatsappTemplate = React.lazy(
	() =>
		import(
			'views/admin/whatsapp/WhatsappSettings/Templates/CreateWhatsappTemplate'
		),
);

// Reports
const Report = React.lazy(() => import('views/admin/reports-v2'));
const TeamDetailsScreen = React.lazy(
	() =>
		import(
			'views/admin/reports-v2/components/lead-report/Teams/TeamDetailsScreen'
		),
);

// Reports V3 (reportsModuleV3)
const ReportsHomeV3 = React.lazy(() => import('views/admin/reportsModuleV3'));
const LeadsReportsV3 = React.lazy(
	() => import('views/admin/reportsModuleV3/leads/LeadsReportsPage'),
);

// Settings
const AdminSetting = React.lazy(() => import('views/admin/adminSetting'));
const Role = React.lazy(() => import('views/admin/role'));
const UserPermission = React.lazy(() => import('views/admin/userPermission'));
const CustomField = React.lazy(() => import('views/admin/customField'));
const TableField = React.lazy(() => import('views/admin/tableField'));
const ChangeImage = React.lazy(() => import('views/admin/image'));
const Validation = React.lazy(() => import('views/admin/validation'));
const LeadSetting = React.lazy(() => import('views/admin/leadSetting'));
const LeadsSetting = React.lazy(() => import('views/admin/leadsSetting/index'));
const LeadInvitation = React.lazy(
	() => import('views/admin/leadsSetting/components/qrCode/index'),
);
const LeadStatus = React.lazy(
	() => import('views/admin/leadsSetting/components/leadStatus/index'),
);
const EmailTemplates = React.lazy(
	() => import('views/admin/adminSetting/emailTemplates/index'),
);
const Agency = React.lazy(() => import('views/admin/agencies'));
const OfficeSettings = React.lazy(
	() => import('views/admin/agencies/OfficeSetting'),
);

const WhatsappCampaignHistory = React.lazy(
	() => import('views/admin/whatsapp/WhatsappCampaign/index'),
);

// Others
const TaskV2 = React.lazy(() => import('views/admin/taskV2'));
const Sip = React.lazy(() => import('views/admin/sip'));
const SipDashboard = React.lazy(
	() => import('views/admin/sip/component/Dashboard'),
);
const SipHistory = React.lazy(
	() => import('views/admin/sip/component/RecordingHistory/index'),
);
const SipSettings = React.lazy(
	() => import('views/admin/sip/component/UserSetting/index'),
);
const SipUserAnalytics = React.lazy(
	() => import('views/admin/sip/component/Analytics/index'),
);
const SipVoipAnalytics = React.lazy(
	() => import('views/admin/sip/component/VoipAnalytics/index'),
);
const SharedSipRecording = React.lazy(
	() => import('views/admin/sip/component/sharedRecording/index'),
);
const Announcement = React.lazy(() => import('views/admin/announcement'));
const CreateAnnouncement = React.lazy(
	() => import('views/admin/announcement/components/v2/CreateAnnouncement'),
);
const AnnouncementHistory = React.lazy(
	() => import('views/admin/announcement/components/History'),
);

const SystemLog = React.lazy(() => import('views/admin/logAction/index'));
const User = React.lazy(() => import('views/admin/users'));
const EditUser = React.lazy(() => import('views/admin/users/EditUser'));
const UserView = React.lazy(() => import('views/admin/users/View'));

// Users V2 (By Arslan)
const UserV2 = React.lazy(() => import('views/admin/users-v2'));
const UserPermissions = React.lazy(
	() => import('views/admin/users-v2/components/UserPermissions'),
);

// Auth
const SignInCentered = React.lazy(() => import('views/auth/signIn'));
const BulkMessage = React.lazy(
	() => import('views/admin/whatsapp/BulkMessage'),
);
const BetaReports = React.lazy(() => import('views/admin/reports/index'));
// Insights module (BI workspace shell + Lead vertical)
const InsightsHome = React.lazy(() =>
	import('views/admin/reports/insights/InsightsHome'),
);
const LeadInsights = React.lazy(() =>
	import('views/admin/reports/insights/modules/lead/LeadInsights'),
);

const DevIntelInbox = React.lazy(
	() => import('views/admin/devIntel/IngestionInbox'),
);
const DevIntelProjects = React.lazy(
	() => import('views/admin/devIntel/ProjectsTable'),
);
const ProjectDetailsPage = React.lazy(
	() => import('views/admin/devIntel/ProjectDetailsPage'),
);
const DevIntelAsk = React.lazy(() => import('views/admin/devIntel/AskAI'));


const routes = [
	{
		name: 'Dev Intel',
		path: '/admin/dev-intel/inbox',
		under: 'dev-intel',
		parent: 'devintel',
		// childId: 'devintel_inbox',
		component: DevIntelInbox,
		// permission: 'devintel:read',
	},
	{
		name: 'Projects',
		path: '/admin/dev-intel/projects',
		under: 'dev-intel',
		parent: 'devintel',
		// childId: 'devintel_projects',
		component: DevIntelProjects,
		// permission: 'devintel:read',
	},
	{
		name: 'Project Details',
		path: '/admin/dev-intel/projects/:id',
		under: 'dev-intel',
		parent: 'devintel',
		// childId: 'devintel_projects',
		component: ProjectDetailsPage,
		// permission: 'devintel:read',
	},
	{
		name: 'Ask AI',
		path: '/admin/dev-intel/ask',
		under: 'dev-intel',
		parent: 'devintel',
		// childId: 'devintel_ask',
		component: DevIntelAsk,
		// permission: 'devintel:read',
	},
	// ========================== Dashboard ==========================
	// {
	// 	// moduleId: 'dashboard',
	// 	name: 'Dashboard',
	// 	layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
	// 	path: '/default',
	// 	icon: <Icon as={MdHome} width='20px' height='20px' color='inherit' />,
	// 	component: MainDashboard,
	// },
	{
		// Dashboard V2 — premium analytics dashboard (v1 untouched at /default)
		name: 'Dashboard V2',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/default',
		icon: <Icon as={MdHome} width='20px' height='20px' color='inherit' />,
		component: DashboardV2,
	},
	// ========================== Admin Layout ==========================
	{
		moduleId: 'leads',
		name: 'Lead',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/lead',
		under: 'lead',
		parent: 'lead',
		icon: (
			<Icon as={MdLeaderboard} width='20px' height='20px' color='inherit' />
		),
		component: LeadScreen,
	},
	// {
	// 	parent: 'leads',
	// 	childId: 'fresh_leads',
	// 	name: 'Fresh Leads',
	// 	layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
	// 	path: '/fresh-leads',
	// 	under: 'fresh-lead',
	// 	component: NewLeadsScreen,
	// },
	{
		parent: 'leads',
		// childId: "leads_setting",
		name: 'Setting',
		name: 'Settings',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/lead_setting',
		path: '/lead_settings',
		component: LeadsSetting,
	},
	{
		parent: 'leads',
		// childId: "qr_settings",
		name: 'Lead Invitation',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/leads-settings/lead-invitation',
		path: '/lead_settings/invitation',
		component: LeadInvitation,
	},
	{
		parent: 'leads',
		// childId: "qr_settings",
		name: 'Lead Status',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/leads-settings/lead-status',
		path: '/lead_settings/status',
		component: LeadStatus,
	},
	{
		parent: 'leads',
		childId: 'lead_analytics',
		name: 'Lead',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/lead_analytics',
		component: LeadAnalytics,
	},
	{
		parent: 'leads',
		// childId: "call_feedbacks",
		name: 'Call Feedback',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/call-feedback',
		component: CallFeedback,
	},
	{
		moduleId: 'leadpool_admin',
		name: 'Lead Pool',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/pool',
		icon: (
			<Icon
				as={MdOutlineAdminPanelSettings}
				width='20px'
				height='20px'
				color='inherit'
			/>
		),
		component: LeadPoolAdmin,
	},
	{
		moduleId: 'leadpool_agents',
		name: 'Leads Pool',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/agent_pool',
		icon: (
			<Icon as={MdPeopleOutline} width='20px' height='20px' color='inherit' />
		),
		component: LeadPoolAgent,
	},
	{
		moduleId: 'buy_new_leads',
		name: 'Buy New Leads',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/buy-new-leads',
		icon: (
			<Icon as={MdAddShoppingCart} width='20px' height='20px' color='inherit' />
		),
		component: BuyNewLeads,
	},
	{
		moduleId: 'deal',
		name: 'Deals',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/deals',
		icon: <Icon as={FaHandshake} width='20px' height='20px' color='inherit' />,
		component: DealsScreen,
	},
	{
		moduleId: 'deal',
		name: 'Close Deals',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/deals/close-deal',
		under: 'deal',
		parentName: 'deal',
		component: CloseDealScreen,
	},
	{
		moduleId: 'deal',
		name: 'Shared Deals',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/deals/shared-deals',
		under: 'deal',
		parentName: 'deal',
		component: SharedDealsScreen,
	},
	{
		moduleId: 'announcement',
		name: 'Announcement',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/announcements',
		icon: <Icon as={MdCampaign} width='20px' height='20px' color='inherit' />,
		component: Announcement,
	},
	{
		moduleId: 'announcement',
		name: 'Announcement',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/announcement/create',
		under: 'announcement',
		parentName: 'announcement',
		component: CreateAnnouncement,
	},

	{
		moduleId: 'announcement',
		name: 'History',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/announcement/history',
		under: 'announcement',
		parentName: 'announcement',
		component: AnnouncementHistory,
	},
	{
		moduleId: 'attendance',
		name: 'Employees',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/attendance/employees',
		under: 'employees',
		parentName: 'Attendance',
		component: Employees,
	},

	// Attendance Routes
	{
		moduleId: 'attendance',
		name: 'Attendance',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/attendance',
		icon: (
			<Icon
				as={FaRegCalendarCheck}
				width='20px'
				height='20px'
				color='inherit'
			/>
		),
		component: AttendanceV2,
	},
	{
		childid: 'attendance',
		name: 'Attendance Dashboard',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/attendance/dashboard',
		under: 'employees',
		parentName: 'Attendance',
		component: AttendanceDashboard,
	},
	{
		childid: 'attendance',
		name: 'Employees',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/attendance/employees',
		under: 'employees',
		parentName: 'Attendance',
		component: Employees,
	},
	{
		childid: 'attendance',
		name: 'Attendance Record',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/attendance/record',
		under: 'attendance-record',
		parentName: 'Attendance',
		component: Records,
	},
	{
		// An employee's attendance is opened from the Employees list AND from a Record row, so holding either action opens it.
		// (This used to read `childid: 'attendance'` - a typo no code reads - which left the page open to everyone.)
		anyOf: [
			['attendance', 'employees'],
			['attendance', 'record'],
		],
		name: 'My Attendance',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/attendance/employees/:id',
		under: 'my-attendance',
		parentName: 'Attendance',
		component: MyAttendance,
	},
	{
		childid: 'attendance',
		name: 'My Attendance',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/attendance/my_attendance',
		under: 'my-attendance',
		parentName: 'Attendance',
		component: MyAttendance,
	},
	{
		moduleId: 'leave',
		name: 'Leave',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/leave-management',
		component: LeaveManagement,
	},
	{
		moduleId: 'invoice',
		name: 'Developer Details',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/developer/:id',
		under: 'developer',
		parentName: 'develoeper',
		component: DeveloperDetails,
	},
	// ------------- Invoice Module Routes ------------------------ //
	{
		moduleId: 'invoice',
		name: 'Invoice',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		icon: (
			<Icon
				as={HiOutlineDocumentReport}
				width='20px'
				height='20px'
				color='inherit'
			/>
		),
		path: '/invoice',
		component: InvoiceModule,
	},
	{
		moduleId: 'invoice',
		name: 'Bank Accounts',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/invoice/bank-account',
		under: 'invoice',
		parentName: 'invoice',
		component: BankAccount,
	},
	{
		moduleId: 'invoice',
		name: 'Developer',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/invoice/developer',
		under: 'invoice',
		parentName: 'invoice',
		component: DeveloperScreen,
	},
	{
		moduleId: 'invoice',
		name: 'Project',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/invoice/project',
		under: 'invoice',
		parentName: 'invoice',
		component: ProjectScreen,
	},
	{
		moduleId: 'invoice',
		// Action-level gate: the page needs invoice:settings.view, not just the module.
		parent: 'invoice',
		childId: 'settings.view',
		name: 'Invoice Settings',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/invoice/settings',
		under: 'invoice',
		parentName: 'invoice',
		component: InvoiceSettings,
	},
	{
		moduleId: 'invoice',
		name: 'Developer Invoices',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'developer-invoices',
		path: '/invoice/developers/invoices/:id',
		parentName: 'Invoice',
		component: DeveloperInvoices,
	},
	{
		moduleId: 'invoice',
		// Every invoice, across developers, with the date / agency / developer filters.
		// Gated by invoice:view; which agencies it shows follows invoice:all_agencies on the server.
		parent: 'invoice',
		childId: 'view',
		name: 'All Invoices',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/invoice/all-invoices',
		under: 'invoice',
		parentName: 'invoice',
		component: AllInvoices,
	},
	{
		moduleId: 'invoice',
		// Action-level gate: a separate capability from generic invoice
		// `create` — a manual (non-deal) invoice is billed to a free-text
		// name, not any Developer, so it needs its own permission.
		parent: 'invoice',
		childId: 'manual.view',
		name: 'Manual Invoices',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/invoice/manual-invoices',
		under: 'invoice',
		parentName: 'invoice',
		component: ManualInvoices,
	},
	{
		moduleId: 'invoice',
		name: 'Single Invoice',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'single-invoice',
		parentName: 'Invoice',
		path: '/invoice/developers/invoices/view/:id',
		component: SingleInvoice,
	},
	{
		moduleId: 'invoice',
		name: 'Invoice Entries',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'invoice-entries',
		parentName: 'Invoice',
		path: '/invoice/developers/invoices/entries/:id',
		component: AddEntry,
	},

	// ********** Payrol routes ************** //
	{
		parent: 'payroll',
		// childId: "salaried_users",

		name: 'Payroll',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/payroll/users',
		component: PayrollSalariedUsers,
	},
	{
		parent: 'payroll',
		// childId: "commission_users",
		name: 'Payroll',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/payroll/commission-users',
		component: PayrollCommissionUsers,
	},
	{
		parent: 'payroll',
		childId: 'salaried_users',
		name: 'Payroll',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/payroll/users/payslip/:userId',
		component: EmployeePayslip,
	},
	{
		parent: 'payroll',
		childId: 'commission_users',
		name: 'Payroll',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/payroll/commission-users/payslip/:userId',
		component: CommissionEmployeePayslip,
	},
	{
		parent: 'payroll',
		childId: 'my_payslip',
		name: 'My Payroll',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/payroll/my-payslip',
		component: UserPayroll,
	},
	{
		moduleId: 'evaluation',
		name: 'Evalution',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/evaluation',
		icon: <Icon as={FaHandshake} width='20px' height='20px' color='inherit' />,
		component: EvaluationScreen,
	},
	{
		parent: 'evaluation',
		childId: 'evaluation_users',
		name: 'User Evalution',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'evaluation',
		path: '/evaluation/user-evaluation',
		parentName: 'evaluation',
		component: UserEvalution,
	},
	{
		parent: 'evaluation',
		childId: 'my_evaluations',
		name: 'My Evalution',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'evaluation',
		path: '/evaluation/my-evaluation',
		parentName: 'evaluation',
		component: MyEvaluation,
	},
	{
		parent: 'evaluation',
		childId: 'evaluation_users',
		name: 'Add Evalution',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'evaluation',
		path: '/evaluation/user-evaluation/role/:roleId/user/:userId',
		parentName: 'evaluation',
		component: EvaluationForm,
	},
	{
		parent: 'evaluation',
		childId: 'evaluation_users',
		name: 'Edit Evalution',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'evaluation',
		path: '/evaluation/edit-user-evaluation/role/:roleId/user/:userId',
		parentName: 'evaluation',
		component: EditEvaluationForm,
	},
	{
		parent: 'evaluation',
		childId: 'settings',
		name: 'Settings',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'evaluation',
		path: '/evaluation/settings',
		parentName: 'evaluation',
		component: EvaluateSettings,
	},
	{
		parent: 'evaluation',
		// childId: "templates",
		name: 'Templates',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'evaluation',
		path: '/evaluation/templates',
		parentName: 'evaluation',
		component: EvaluationTemplates,
	},
	{
		parent: 'evaluation',
		// childId: "teams",
		name: 'Teams',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'evaluation',
		path: '/evaluation/teams',
		parentName: 'evaluation',
		component: Teams,
	},

	//****** Finance routes *********//
	{
		moduleId: 'expense',
		name: 'Expense',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/finance',
		icon: <Icon as={FaHandshake} width='20px' height='20px' color='inherit' />,
		component: FinanceScreen,
	},
	{
		parent: 'expense',
		childId: 'incoming_cash',
		name: 'Incoming Cash',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance',
		path: '/finance/incoming-cash',
		parentName: 'finance',
		component: IncomingCash,
	},
	{
		parent: 'expense',
		childId: 'outgoing_cash',
		name: 'Outgoing Cash',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance',
		path: '/finance/outgoing-cash',
		parentName: 'finance',
		component: OutgoingCash,
	},
	// {
	//   parent: "expense",
	//   childId: "employee_loans",
	//   name: "Employee Loans",
	//   layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
	//   under: "finance",
	//   path: "/employee-loans",
	//   parentName: "finance",
	//   component: EmployeeLoans,
	// },
	// {
	//   parent: "expense",
	//   childId: "employee_loans",
	//   name: "Employee Loan Details",
	//   layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
	//   under: "finance",
	//   path: "/employee-loans/:id",
	//   parentName: "finance",
	//   component: EmployeeLoanDetails,
	// },
	{
		moduleId: 'employee_loans',
		name: 'Employee Loans',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/employee-loans',
		icon: <Icon as={RiBillLine} width='20px' height='20px' color='inherit' />,
		component: EmployeeLoans,
	},
	{
		parent: 'employee_loans',
		name: 'Employee Loan Details',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/employee-loans/:id',
		component: EmployeeLoanDetails,
	},
	{
		parent: 'expense',
		childId: 'settings',
		name: 'Finance Settings',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance',
		path: '/finance/settings',
		parentName: 'finance',
		component: FinanceSettings,
	},

	//****** Finance Core (accounting / GL) routes *********//
	{
		moduleId: 'finance_core',
		name: 'Finance Core',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/finance-core',
		icon: <Icon as={FaHandshake} width='20px' height='20px' color='inherit' />,
		component: FinanceCoreDashboard,
	},
	{
		parent: 'finance_core',
		childId: 'chart_accounts_view',
		name: 'Chart of Accounts',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/chart-of-accounts',
		parentName: 'finance-core',
		component: ChartOfAccounts,
	},
	{
		parent: 'finance_core',
		childId: 'journal_entries_view',
		name: 'Journal Entries',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/journal-entries',
		parentName: 'finance-core',
		component: JournalEntries,
	},
	{
		parent: 'finance_core',
		childId: 'invoices_view',
		name: 'Developer Invoices',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/developer-invoices',
		parentName: 'finance-core',
		component: DeveloperInvoicesFC,
	},
	{
		parent: 'finance_core',
		childId: 'collections_view',
		name: 'Payment Collections',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/collections',
		parentName: 'finance-core',
		component: CollectionsFC,
	},
	{
		parent: 'finance_core',
		childId: 'cheques_manage',
		name: 'Cheques',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/cheques',
		parentName: 'finance-core',
		component: ChequesFC,
	},
	{
		parent: 'finance_core',
		childId: 'reports_finance_view',
		name: 'Receivables Aging',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/receivables',
		parentName: 'finance-core',
		component: ReceivablesFC,
	},
	{
		parent: 'finance_core',
		childId: 'commissions_view',
		name: 'Commission Center',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/commissions',
		parentName: 'finance-core',
		component: CommissionCenterFC,
	},
	{
		parent: 'finance_core',
		childId: 'commissions_view',
		name: 'Commission Batches',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/commission-batches',
		parentName: 'finance-core',
		component: CommissionBatchesFC,
	},
	{
		parent: 'finance_core',
		childId: 'reports_finance_view',
		name: 'Commission Report',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/commission-report',
		parentName: 'finance-core',
		component: CommissionReportFC,
	},
	{
		parent: 'finance_core',
		childId: 'expenses_view',
		name: 'Expenses',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/expenses',
		parentName: 'finance-core',
		component: ExpensesFC,
	},
	{
		parent: 'finance_core',
		childId: 'suppliers_view',
		name: 'Suppliers',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/suppliers',
		parentName: 'finance-core',
		component: SuppliersFC,
	},
	{
		parent: 'finance_core',
		childId: 'purchase_requests_view',
		name: 'Procurement',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/purchase-requests',
		parentName: 'finance-core',
		component: ProcurementFC,
	},
	{
		parent: 'finance_core',
		childId: 'purchase_orders_view',
		name: 'Purchase Orders',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/purchase-orders',
		parentName: 'finance-core',
		component: PurchaseOrdersFC,
	},
	{
		parent: 'finance_core',
		childId: 'ap_invoices_view',
		name: 'Supplier Invoices',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/ap-invoices',
		parentName: 'finance-core',
		component: APInvoicesFC,
	},
	{
		parent: 'finance_core',
		childId: 'payments_out_view',
		name: 'Payments Out',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/payment-vouchers',
		parentName: 'finance-core',
		component: PaymentsOutFC,
	},
	{
		parent: 'finance_core',
		childId: 'employee_finance_view',
		name: 'Employee Finance',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/employee-finance',
		parentName: 'finance-core',
		component: EmployeeFinanceFC,
	},
	{
		parent: 'finance_core',
		childId: 'payroll_postings_view',
		name: 'Payroll Postings',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/payroll-postings',
		parentName: 'finance-core',
		component: PayrollPostingsFC,
	},
	{
		parent: 'finance_core',
		childId: 'treasury_view',
		name: 'Treasury',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/treasury',
		parentName: 'finance-core',
		component: TreasuryFC,
	},
	{
		parent: 'finance_core',
		childId: 'treasury_view',
		name: 'Bank',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/bank',
		parentName: 'finance-core',
		component: BankFC,
	},
	{
		parent: 'finance_core',
		childId: 'cards_view',
		name: 'Corporate Cards',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/cards',
		parentName: 'finance-core',
		component: CardsFC,
	},
	{
		parent: 'finance_core',
		childId: 'obligations_view',
		name: 'Obligations',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/obligations',
		parentName: 'finance-core',
		component: ObligationsFC,
	},
	{
		parent: 'finance_core',
		childId: 'contracts_view',
		name: 'Contracts',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/contracts',
		parentName: 'finance-core',
		component: ContractsFC,
	},
	{
		parent: 'finance_core',
		childId: 'treasury_view',
		name: 'Treasury Reports',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/treasury-reports',
		parentName: 'finance-core',
		component: TreasuryReportsFC,
	},
	{
		parent: 'finance_core',
		childId: 'recognition_view',
		name: 'Recognition',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/recognitions',
		parentName: 'finance-core',
		component: RecognitionsFC,
	},
	{
		parent: 'finance_core',
		childId: 'assets_view',
		name: 'Fixed Assets',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/assets',
		parentName: 'finance-core',
		component: AssetsFC,
	},
	{
		parent: 'finance_core',
		childId: 'inventory_view',
		name: 'Inventory',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/inventory',
		parentName: 'finance-core',
		component: InventoryFC,
	},
	{
		parent: 'finance_core',
		childId: 'assets_view',
		name: 'Asset Reports',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/asset-reports',
		parentName: 'finance-core',
		component: AssetReportsFC,
	},
	{
		parent: 'finance_core',
		childId: 'allocations_view',
		name: 'Cost Allocation',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/allocations',
		parentName: 'finance-core',
		component: AllocationsFC,
	},
	{
		parent: 'finance_core',
		childId: 'allocations_view',
		name: 'Projects',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/projects',
		parentName: 'finance-core',
		component: ProjectsFC,
	},
	{
		parent: 'finance_core',
		childId: 'equity_view',
		name: 'Equity',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/equity',
		parentName: 'finance-core',
		component: EquityFC,
	},
	{
		parent: 'finance_core',
		childId: 'credit_view',
		name: 'Credit Control',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/credit-control',
		parentName: 'finance-core',
		component: CreditControlFC,
	},
	{
		parent: 'finance_core',
		childId: 'tax_view',
		name: 'Tax',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/tax-codes',
		parentName: 'finance-core',
		component: TaxCodesFC,
	},
	{
		parent: 'finance_core',
		childId: 'tax_view',
		name: 'Tax Returns',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/tax-returns',
		parentName: 'finance-core',
		component: TaxReturnsFC,
	},
	{
		parent: 'finance_core',
		childId: 'fx_view',
		name: 'Currency',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/currency',
		parentName: 'finance-core',
		component: CurrencyFC,
	},
	{
		parent: 'finance_core',
		childId: 'periods_manage',
		name: 'Period Close',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/period-close',
		parentName: 'finance-core',
		component: PeriodCloseFC,
	},
	{
		parent: 'finance_core',
		childId: 'imports_run',
		name: 'Imports',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/imports',
		parentName: 'finance-core',
		component: ImportsFC,
	},
	{
		parent: 'finance_core',
		childId: 'risk_view',
		name: 'Risk & Controls',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/risk',
		parentName: 'finance-core',
		component: RiskFC,
	},
	{
		parent: 'finance_core',
		childId: 'finance_dashboard_view',
		name: 'Dashboards',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/dashboards',
		parentName: 'finance-core',
		component: DashboardsFC,
	},
	{
		parent: 'finance_core',
		childId: 'reports_finance_view',
		name: 'Profitability',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/profitability',
		parentName: 'finance-core',
		component: ProfitabilityFC,
	},
	{
		parent: 'finance_core',
		childId: 'finance_dashboard_view',
		name: 'Costing & Performance',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/costing-dashboard',
		parentName: 'finance-core',
		component: CostingDashboardFC,
	},
	{
		parent: 'finance_core',
		childId: 'reports_finance_view',
		name: 'Financial Reports',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/financial-reports',
		parentName: 'finance-core',
		component: FinancialReportsFC,
	},
	{
		parent: 'finance_core',
		childId: 'reports_finance_view',
		name: 'Budgets',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/budgets',
		parentName: 'finance-core',
		component: BudgetsFC,
	},
	{
		parent: 'finance_core',
		childId: 'approvals_view',
		name: 'Approvals',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/approvals',
		parentName: 'finance-core',
		component: ApprovalsFC,
	},
	{
		parent: 'finance_core',
		childId: 'settings_manage',
		name: 'Finance Governance',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/governance',
		parentName: 'finance-core',
		component: GovernanceFC,
	},
	{
		parent: 'finance_core',
		childId: 'finance_dashboard_view',
		name: 'Financial Calendar',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		under: 'finance-core',
		path: '/finance-core/calendar',
		parentName: 'finance-core',
		component: FinancialCalendarFC,
	},


	// -----------------------------Admin setting-------------------------------------
	{
		name: "Admin Setting",
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		parentName: "admin",
		under: "admin",
		path: "/admin-setting",
		component: AdminSetting,
	},
	{
		moduleId: 'admin_settings',
		name: 'Configuration',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/admin-setting/configuration',
		under: 'admin-setting',
		component: SecretConfiguration,
	},
	{
		// No `moduleId`: the page checks its own keys (ad_conversions_view) plus
		// the admin tier, as the API does — the module flag alone is not enough.
		name: 'Ad Pixels & Conversions',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/admin-setting/ad-conversions',
		under: 'admin-setting',
		component: AdConversions,
	},
	{
		moduleId: 'admin_settings',
		name: 'Email Templates',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/admin-setting/email-templates',
		under: 'admin-setting',
		component: EmailTemplates,
	},
	{
		// moduleId: "integrations",
		name: "Integrations",
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: "/admin-setting/integrations",
		under: "admin",
		component: IntegrationsGuide,
	},
	{
		// moduleId: "integrations",
		name: "Leads Integration",
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: "/admin-setting/integrations/leads",
		component: LeadsIntegration,
	},
	{
		// moduleId: "integrations",
		name: "Listings Integration",
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: "/admin-setting/integrations/listings",
		component: ListingsIntegration,
	},

	// ------------- Task Routes ------------------------
	{
		moduleId: 'task',
		name: 'Task',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/task',
		icon: <Icon as={FaTasks} width='20px' height='20px' color='inherit' />,
		component: TaskV2,
	},

	// ------------- Hiring Routes -----------------------

	{
		moduleId: 'hiring',
		name: 'Hiring',
		path: '/hiring',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		icon: (
			<Icon as={FaClipboardUser} width='20px' height='20px' color='inherit' />
		),
		component: Hiring,
	},
	{
		moduleId: 'hiring',
		name: 'Hiring Dashboard',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/hiring/dasboard',
		under: 'Hiring',
		parentName: 'Hiring',
		component: HiringDashboard,
	},
	{
		moduleId: 'hiring',
		name: 'Candidates',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/hiring/candidates',
		under: 'candidates',
		parentName: 'Hiring',
		component: Candidates,
	},
	{
		moduleId: 'hiring',
		name: 'Short Listed',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/hiring/short-listed',
		under: 'shortListed',
		parentName: 'Hiring',
		component: ShortListedCandidates,
	},
	{
		moduleId: 'hiring',
		name: 'Interview',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/hiring/interview/:interviewId',
		under: 'interview',
		parentName: 'Hiring',
		component: InterviewScreen,
	},
	{
		moduleId: 'hiring',
		name: 'Interviewed Candidates',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/hiring/interviewed-candidates',
		under: 'interviewCandidates',
		parentName: 'Hiring',
		component: InterviewedCandidates,
	},
	{
		moduleId: 'hiring',
		name: 'Multi-Round Candidates',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/hiring/multi-round',
		under: 'Hiring',
		parentName: 'Hiring',
		component: InterviewedRound,
	},
	{
		moduleId: 'hiring',
		name: 'Offer Letter',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/hiring/interviewed-candidates/offer-letter/:id',
		under: 'offerLetter',
		parentName: 'Hiring',
		component: OfferLetter,
	},
	{
		moduleId: 'hiring',
		name: 'Offer View',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/hiring/interviewed-candidates/offer-letter/view/:id',
		under: 'offerView',
		parentName: 'Hiring',
		component: OfferView,
	},
	{
		name: 'Positions',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/hiring/settings',
		under: 'positions',
		parentName: 'Hiring',
		component: Positions,
	},

	// ------------- Phone Routes ------------------------
	{
		moduleId: 'sip',
		name: 'Call Logs',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/sip',
		icon: <Icon as={FaPhone} width='20px' height='20px' color='inherit' />,
		component: Sip,
	},
	{
		// parent: 'sip',
		moduleId: 'sip',
		name: 'Dashboard',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/sip/dashboard',
		under: 'Sip',
		component: SipDashboard,
	},
	{
		parent: 'sip',
		childId: 'call_history',
		name: 'Call history',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/sip/history',
		under: 'Sip',
		component: SipHistory,
	},
	{
		parent: 'sip',
		// childId: 'leaderboard',
		name: 'Leaderboard',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/sip/leaderboard',
		under: 'Sip',
		component: SipLeaderboard,
	},
	{
		parent: 'sip',
		childId: 'my_recordings',
		name: 'My Recordings',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/sip/my-recordings',
		under: 'Sip',
		component: SipMyRecordings,
	},
	{
		childId: 'user_settings',
		name: 'User settings',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/sip/settings',
		under: 'Sip',
		parentName: 'Sip',
		component: SipSettings,
	},
	{
		childId: 'user_analytics',
		name: 'User Settings',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/sip/user-analytics',
		under: 'Sip',
		parentName: 'Sip',
		component: SipUserAnalytics,
	},
	{
		parent: 'sip',
		childId: 'voip_analytics',
		name: 'Voip Analatics',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/sip/voip-analatics',
		under: 'Sip',
		parentName: 'Sip',
		component: SipVoipAnalytics,
	},
	{
		childId: 'shared_recordings',
		name: 'Shared Recoding',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/sip/shared-recording',
		under: 'Sip',
		parentName: 'Sip',
		component: SharedSipRecording,
	},

	// Listing --------------------------------------
	{
		moduleId: 'listing',
		name: 'Listing',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/listing',
		icon: <Icon as={FaList} width='20px' height='20px' color='inherit' />,
		component: Listing,
	},
	{
		moduleId: 'listing',
		name: 'Adding Listing',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/listing/my-listings/add-listing',
		under: 'listing',
		parentName: 'Listing',
		component: AddListing,
	},
	{
		moduleId: 'listing',
		name: 'View Listing',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/listing/view-listing/:id',
		under: 'listing',
		parentName: 'Listing',
		component: ViewListing,
	},
	{
		moduleId: 'listing',
		name: 'Update Listing',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/listing/my-listings/update/:id',
		under: 'listing',
		parentName: 'Listing',
		component: UpdateListing,
	},
	{
		moduleId: 'listing',
		name: 'Update Listing',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/listing/all-listings/update/:id',
		under: 'listing',
		parentName: 'Listing',
		component: UpdateListing,
	},
	{
		moduleId: 'listing',
		name: 'Listing Setting',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/listing/settings',
		under: 'listing',
		parentName: 'Listing',
		component: SettingPage,
	},
	{
		moduleId: 'listing',
		name: 'Sub Unit Types',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/listing/settings/sub-unit-types',
		under: 'listing',
		parentName: 'Listing',
		component: SubUnitType,
	},
	{
		moduleId: 'listing',
		name: 'All Listings',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/listing/all-listings',
		under: 'listing',
		parentName: 'Listing',
		component: AllListing,
	},
	{
		childId: 'client_listing',
		parent: 'listing',
		name: 'Client Listings',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/listing/client-listings',
		under: 'listing',
		parentName: 'Listing',
		component: ClientListings,
	},
	{
		// Opened from a card on the Client Listings page, so it follows that page's action.
		parent: 'listing',
		childId: 'client_listing',
		name: 'View Client Listings',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/listing/client-listings/:id',
		under: 'listing',
		parentName: 'Listing',
		component: PropertyView,
	},
	{
		moduleId: 'listing',
		name: 'My Listings',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/listing/my-listings',
		under: 'listing',
		parentName: 'Listing',
		component: MyListing,
	},
	{
		moduleId: 'listing',
		name: 'Pendings Listing',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/listing/pending-listing',
		under: 'listing',
		parentName: 'Listing',
		component: PendingListing,
	},
	{
		moduleId: 'listing',
		name: 'Pending View Request',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/listing/pending-view-listing',
		under: 'listing',
		parentName: 'Listing',
		component: PendingViewRequest,
	},
	{
		moduleId: 'listing',
		name: 'Approved View Request',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/listing/approved-view-listing',
		under: 'listing',
		parentName: 'Listing',
		component: ApprovedViewRequest,
	},
	{
		moduleId: 'listing',
		name: 'Rejected View Request',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/listing/reject-view-listing',
		under: 'listing',
		parentName: 'Listing',
		component: RejectedViewRequest,
	},
	// View Request Listing routes ----------
	{
		moduleId: 'listing',
		name: 'View Request Listing',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/listing/view-request-listing',
		under: 'listing',
		parentName: 'Listing',
		icon: <Icon as={FaList} width='20px' height='20px' color='inherit' />,
		component: ViewRequestListing,
	},

	{
		moduleId: 'view_request_listing',
		name: 'Pending View Request',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/view-request-listing/pending-view-listing',
		under: 'view_request_listing',
		parentName: 'Listing',
		component: PendingViewRequest,
	},
	{
		moduleId: 'view_request_listing',
		name: 'Approved View Request',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/view-request-listing/approved-view-listing',
		under: 'view_request_listing',
		parentName: 'Listing',
		component: ApprovedViewRequest,
	},
	{
		moduleId: 'listing',
		name: 'Rejected View Request',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/view-request-listing/reject-view-listing',
		under: 'view_request_listing',
		parentName: 'Listing',
		component: RejectedViewRequest,
	},
	// Survey Routes

	{
		moduleId: 'survey',
		name: 'Survey',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/survey',
		icon: <Icon as={FaSquarePlus} width='20px' height='20px' color='inherit' />,
		component: Survey,
	},
	{
		moduleId: 'survey',
		name: 'Dashboard',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/survey/dashboard',
		under: 'Survey',
		parentName: 'Survey',
		component: SurveyDashboard,
	},
	{
		moduleId: 'survey',
		name: 'All Surveys',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/survey/all-surveys',
		under: 'Survey',
		parentName: 'Survey',
		component: SurveyManage,
	},
	{
		moduleId: 'survey',
		name: 'Survey Board',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/survey/dashboard/survey-leader-board',
		under: 'Survey',
		parentName: 'Survey',
		component: LeaderBoard,
	},
	{
		moduleId: 'survey',
		name: 'Create Survey',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/survey/create',
		under: 'Survey',
		parentName: 'Survey',
		component: CreateSurvey,
	},
	{
		moduleId: 'survey',
		name: 'view Survey',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/survey/all-surveys/view-survey/:id',
		under: 'Survey',
		parentName: 'Survey',
		component: ViewSurveyResponse,
	},
	{
		moduleId: 'survey',
		name: 'Take Survey',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/survey/all-surveys/take-survey/:id',
		under: 'Survey',
		parentName: 'Survey',
		component: TakeSurvey,
	},
	// {
	// 	name: 'whatsapp chat',
	// 	layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
	// 	path: '/whatsapp-v2',
	// 	icon: <Icon as={FaWhatsapp} width='20px' height='20px' color='inherit' />,
	// 	component: Whatsapp,
	// },

	// **** Whatsapp **** //
	// V2 routes{
	{
		childId: 'whatsapp_beta',
		name: 'Whatsapp',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/whatsapp/instances',
		parent: 'whatsapp',
		icon: <Icon as={FaWhatsapp} width='20px' height='20px' color='inherit' />,
		component: WhatsappInstances,
	},
	{
		childId: 'whatsapp_beta',
		name: 'User Whatsapp',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/whatsapp/instances/:id',
		parent: 'whatsapp',
		component: UserWhatsappInstance,
	},

	// V1 routes
	{
		childId: 'whatsapp_chats',
		name: 'Whatsapp',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/whatsapp/chats',
		parent: 'whatsapp',
		icon: <Icon as={FaWhatsapp} width='20px' height='20px' color='inherit' />,
		component: AdminWhatsapp,
	},
	{
		childId: 'whatsapp_chats',
		name: 'Whatsapp Chat',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/whatsapp/chats/:id',
		parent: 'whatsapp',
		component: UserWhatsappChat,
	},
	{
		childId: 'whatsapp_chats',
		name: 'Whatsapp Chat',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/whatsapp/chat',
		parent: 'whatsapp',
		component: UserWhatsappChat,
	},
	{
		// ★ `whatsapp_beta`, NOT the `whatsapp_chats` this comment used to
		// name: the route renders UserWhatsappInstance, the same component as
		// `/whatsapp/instances/:id`, which is gated on `whatsapp_beta`, and the
		// sidebar lists Instances under that key too. The commented value looks
		// like a copy from the chat block above it.
		childId: 'whatsapp_beta',
		name: 'Whatsapp Instance',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/whatsapp/instance',
		parent: 'whatsapp',
		component: UserWhatsappInstance,
	},
	{
		childId: 'whatsapp_campaigns',
		name: 'Bulk Messages',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/whatsapp/bulk-messages',
		parent: 'whatsapp',
		component: BulkMessage,
	},

	{
		// Two actions of one module share this page as tabs, so EITHER admits the
		// caller; the page then shows only the tabs they hold.
		anyOf: [
			['whatsapp', 'whatsapp_settings'],
			['whatsapp', 'settings.view'],
		],
		name: 'Whatsapp Settings',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/whatsapp/settings',
		parent: 'whatsapp',
		component: WhatsappSettingsHub,
	},

	{
		childId: 'whatsapp_settings',
		name: 'Whatsapp Templates',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/whatsapp/settings/message_templates/:businessId',
		parent: 'whatsapp',
		component: WhatsappTemplates,
	},

	{
		childId: 'whatsapp_settings',
		name: 'Whatsapp Templates',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/whatsapp/settings/message_templates/:businessId/create_template',
		parent: 'whatsapp',
		component: CreateWhatsappTemplate,
	},
	{
		// Gated like its sidebar entry and its "Bulk Messages" sibling. Without a
		// childId this route bypassed permission filtering entirely.
		childId: 'whatsapp_campaigns',
		name: 'Campaign History',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/whatsapp/campaigns',
		parent: 'whatsapp',
		component: WhatsappCampaignHistory,
	},

	// ------------- Roles Routes ------------------------

	{
		moduleId: 'admin_settings',
		name: 'Roles',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/role',
		under: 'role',
		icon: (
			<Icon
				as={FaCreativeCommonsBy}
				width='20px'
				height='20px'
				color='inherit'
			/>
		),
		component: Role,
	},
	{
		moduleId: 'admin_settings',
		name: 'User Permission',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/user-permission/:id',
		under: 'role',
		component: UserPermission,
	},
	{
		moduleId: 'admin_settings',
		name: 'Custom Fields',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/custom-Fields',
		under: 'customField',
		icon: <Icon as={FaWpforms} width='20px' height='20px' color='inherit' />,
		component: CustomField,
	},
	{
		moduleId: 'admin_settings',
		name: 'Change Images',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/change-images',
		under: 'image',
		icon: (
			<Icon
				as={FaCreativeCommonsBy}
				width='20px'
				height='20px'
				color='inherit'
			/>
		),
		component: ChangeImage,
	},
	{
		moduleId: 'admin_settings',
		name: 'Validation',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/validations',
		under: 'Validation',
		icon: (
			<Icon
				as={FaCreativeCommonsBy}
				width='20px'
				height='20px'
				color='inherit'
			/>
		),
		component: Validation,
	},
	{
		moduleId: 'admin_settings',
		name: 'Table Fields',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/table-field',
		under: 'tableField',
		icon: <Icon as={FaWpforms} width='20px' height='20px' color='inherit' />,
		component: TableField,
	},
	// // ------------- Text message Routes ------------------------
	// {
	// 	moduleId: 'reports',
	// 	name: 'Reports',
	// 	layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
	// 	path: '/reporting-analytics',
	// 	icon: (
	// 		<Icon
	// 			as={MdInsertChartOutlined}
	// 			width='20px'
	// 			height='20px'
	// 			color='inherit'
	// 		/>
	// 	),
	// 	component: Report,
	// },
	{
		parent: 'reports',
		childId: 'report',
		name: 'Reports',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/reporting-analytics/reports',
		icon: (
			<Icon
				as={MdInsertChartOutlined}
				width='20px'
				height='20px'
				color='inherit'
			/>
		),
		component: Report,
	},
	{
		parent: 'reports',
		childId: 'advance_reports',
		name: 'Advance Reports',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/reporting-analytics/advance-reports',
		icon: (
			<Icon
				as={MdInsertChartOutlined}
				width='20px'
				height='20px'
				color='inherit'
			/>
		),
		component: BetaReports,
	},
	{
		parent: 'reports',
		childId: 'advance_reports',
		name: 'Report detail',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/reporting-analytics/advance-reports/:groupKey/:slug',
		component: BetaReports,
	},
	// ---------------- Insights (BI workspace) -------------------------
	// Gated at MODULE level (moduleId: 'reports') — same pattern as the working
	// "Reports V3" route — so it opens for anyone with the reports module. We
	// deliberately do NOT gate on a fresh `reports:insights` action here: the
	// layout's route filter would drop the route for any session whose
	// permission map predates that action, bouncing the user to /default.
	{
		moduleId: 'reports',
		name: 'Insights',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/reporting-analytics/insights',
		icon: (
			<Icon
				as={MdInsertChartOutlined}
				width='20px'
				height='20px'
				color='inherit'
			/>
		),
		component: InsightsHome,
	},
	{
		moduleId: 'reports',
		name: 'Lead Insights',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/reporting-analytics/insights/lead',
		parent: 'Insights',
		under: 'Insights',
		component: LeadInsights,
	},
	{
		moduleId: 'reports',
		name: 'Team Details',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/reporting-analytics/team-details/:id',
		parent: 'Reports',
		under: 'Reports',
		component: TeamDetailsScreen,
	},
	// ------------- Reports V3 (reportsModuleV3) ------------------------
	{
		moduleId: 'reports',
		name: 'Reports V3',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/reports-v3',
		icon: (
			<Icon
				as={MdInsertChartOutlined}
				width='20px'
				height='20px'
				color='inherit'
			/>
		),
		component: ReportsHomeV3,
	},
	{
		moduleId: 'reports',
		name: 'Leads Reports V3',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/reports-v3/leads',
		parent: 'Reports V3',
		under: 'Reports V3',
		component: LeadsReportsV3,
	},
	{
		moduleId: 'system_log',
		name: 'System Log',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/system-log',
		icon: (
			<Icon
				as={MdOutlineLaptopMac}
				width='20px'
				height='20px'
				color='inherit'
			/>
		),
		component: SystemLog,
	},
	// ------------- Roles Routes ------------------------
	{
		moduleId: 'admin_settings',
		name: 'Roles',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/role',
		under: 'role',
		icon: (
			<Icon
				as={FaCreativeCommonsBy}
				width='20px'
				height='20px'
				color='inherit'
			/>
		),
		component: Role,
	},
	{
		moduleId: 'admin_settings',
		name: 'User Permission',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/user-permission/:id/:roleName',
		under: 'role',
		component: UserPermission,
	},
	{
		moduleId: 'admin_settings',
		name: 'Custom Fields',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/custom-Fields',
		under: 'customField',
		icon: <Icon as={FaWpforms} width='20px' height='20px' color='inherit' />,
		component: CustomField,
	},
	{
		moduleId: 'admin_settings',
		name: 'Change Images',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/change-images',
		under: 'image',
		icon: (
			<Icon
				as={FaCreativeCommonsBy}
				width='20px'
				height='20px'
				color='inherit'
			/>
		),
		component: ChangeImage,
	},
	{
		moduleId: 'admin_settings',
		name: 'Validation',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/validations',
		under: 'Validation',
		icon: (
			<Icon
				as={FaCreativeCommonsBy}
				width='20px'
				height='20px'
				color='inherit'
			/>
		),
		component: Validation,
	},
	{
		moduleId: 'admin_settings',
		name: 'Table Fields',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/table-field',
		under: 'tableField',
		icon: <Icon as={FaWpforms} width='20px' height='20px' color='inherit' />,
		component: TableField,
	},
	// // ------------- Text message Routes ------------------------
	{
		moduleId: 'reports',
		name: 'Reports',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/reporting-analytics',
		icon: (
			<Icon
				as={MdInsertChartOutlined}
				width='20px'
				height='20px'
				color='inherit'
			/>
		),
		component: Report,
	},
	{
		moduleId: 'reports',
		name: 'Team Details',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/reporting-analytics/team-details/:id',
		parent: 'Reports',
		under: 'Reports',
		component: TeamDetailsScreen,
	},
	{
		moduleId: 'system_log',
		name: 'System Log',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/system-log',
		icon: (
			<Icon
				as={MdOutlineLaptopMac}
				width='20px'
				height='20px'
				color='inherit'
			/>
		),
		component: SystemLog,
	},

	// ------------- user Routes ------------------------
	{
		moduleId: 'users',
		name: 'Users',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/users',
		under: 'user',
		icon: <Icon as={HiUsers} width='20px' height='20px' color='inherit' />,
		component: User,
	},
	{
		moduleId: 'users',
		name: 'Users',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/admin-setting/users',
		under: 'user',
		icon: <Icon as={HiUsers} width='20px' height='20px' color='inherit' />,
		// component: User,
		component: UserV2,
	},
	{
		name: 'User View',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		parentName: 'Email',
		under: 'user',
		path: '/users/:id',
		component: UserView,
	},
	{
		name: 'User View',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		parentName: 'Email',
		under: 'user',
		path: '/users/edit/:id',
		component: EditUser,
	},

	// ------------- user v2 Routes ------------------------
	{
		moduleId: 'users',
		name: 'Users V2',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/users-v2',
		under: 'user',
		icon: <Icon as={HiUsers} width='20px' height='20px' color='inherit' />,
		component: UserV2,
	},
	{
		// moduleId: 'users',
		name: 'Users V2',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/users-v2/:id',
		under: 'user',
		icon: <Icon as={HiUsers} width='20px' height='20px' color='inherit' />,
		component: UserProfileDetails,
	},
	{
		// moduleId: 'users',
		name: 'User Permissions',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/users-v2/permissions/:id',
		under: 'user',
		icon: <Icon as={HiUsers} width='20px' height='20px' color='inherit' />,
		component: UserPermissions,
	},

	{
		moduleId: 'admin_settings',
		name: 'Whatsapp Settings',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/admin-setting/whatsapp/settings',
		under: '/admin-setting',
		component: WhatsappSettingsHub,
	},
	{
		moduleId: 'admin_settings',
		name: 'Lead Settings',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/lead-settings',
		under: 'lead-settings',
		component: LeadSetting,
	},
	{
		moduleId: 'admin_settings',
		name: 'Agencies',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/agencies',
		under: 'agencies',
		component: Agency,
	},
	{
		moduleId: 'admin_settings',
		name: 'Office Settings',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/office-settings/:id',
		under: '-settings',
		component: OfficeSettings,
	},
	{
		moduleId: 'admin_settings',
		name: 'Configuration',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/admin-setting/configuration',
		under: 'admin-setting',
		component: SecretConfiguration,
	},
	// ========================== auth layout ==========================
	{
		name: 'Sign In',
		layout: '/auth',
		path: '/sign-in',
		icon: <Icon as={MdLock} width='20px' height='20px' color='inherit' />,
		component: SignInCentered,
	},

	// ===== Meta WhatsApp V2 =====
	// Both entries declare parent AND childId. A route missing childId bypasses
	// permission filtering entirely and would appear for every user.
	{
		parent: 'whatsapp',
		childId: 'inbox.view',
		name: 'WhatsApp',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/meta-whatsapp',
		component: MetaWhatsappInbox,
	},
	{
		parent: 'whatsapp',
		childId: 'template.view',
		name: 'WhatsApp',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: "/whatsapp/settings/meta/templates/:channelId",
		component: MetaWhatsappTemplates ,
	},
	{
		parent: 'whatsapp',
		childId: 'template.create',
		name: 'WhatsApp',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: "/whatsapp/settings/meta/templates/:channelId/create",
		component: CreateMetaWhatsappTemplate ,
	},
	// ★ MOST SPECIFIC FIRST: `/campaigns/:id/edit` before `/campaigns/:id`
	// before `/campaigns`, or the broader pattern swallows the narrower one and
	// the wizard renders the dashboard.
	{
		parent: 'whatsapp',
		childId: 'campaign.create',
		name: 'Edit campaign',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/meta-campaigns/:id/edit',
		component: CampaignWizard,
	},
	{
		parent: 'whatsapp',
		childId: 'campaign.view',
		name: 'Campaign',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/meta-campaigns/:id',
		component: CampaignDetail,
	},
	{
		parent: 'whatsapp',
		childId: 'campaign.view',
		name: 'Meta Campaigns',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/meta-campaigns',
		component: CampaignsList,
	},
	{
		// The pre-merge address. Redirects into the tabbed page rather than
		// rendering a second copy of the same screen.
		parent: 'whatsapp',
		childId: 'settings.view',
		name: 'WhatsApp Settings',
		layout: [ROLE_PATH.superAdmin, ROLE_PATH.user],
		path: '/meta-whatsapp/settings',
		component: MetaSettingsRedirect,
	},
];

export default routes;
